'use client';

import { useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import {
  DEFAULT_SCANNING_SCHEDULE,
  ScanningConfigurationCard,
  type ScanningConfigurationChange,
} from '../../settings/automation/ScanningConfigurationCard';
import { SettingsSection } from '../../settings/SettingsSection';
import {
  hasExternalNetworkSchedule,
  resolveExecutionScheduleUpdates,
  type ExternalNetworkSettingsPersistRequest,
} from './external-settings.model';

export type ExternalNetworkExecutionSettingsProps = {
  surface: Pick<ExternalNetworkDto, 'executionSchedule' | 'smokeTestEnabled'>;
  isReadOnly: boolean;
  isUpdating: boolean;
  onPersist: (
    request: ExternalNetworkSettingsPersistRequest,
  ) => Promise<boolean>;
};

export function ExternalNetworkExecutionSettings({
  surface,
  isReadOnly,
  isUpdating,
  onPersist,
}: ExternalNetworkExecutionSettingsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalSettings.execution',
  );
  const [syncedSchedule, setSyncedSchedule] = useState(
    surface.executionSchedule,
  );
  const [limitScheduleEnabled, setLimitScheduleEnabled] = useState(() =>
    hasExternalNetworkSchedule(surface.executionSchedule),
  );

  if (syncedSchedule !== surface.executionSchedule) {
    setSyncedSchedule(surface.executionSchedule);
    setLimitScheduleEnabled(
      hasExternalNetworkSchedule(surface.executionSchedule),
    );
  }

  const handleExecutionChange = (change: ScanningConfigurationChange) => {
    if (
      typeof change.smokeTestEnabled !== 'boolean' &&
      typeof change.limitScheduleEnabled === 'boolean'
    ) {
      setLimitScheduleEnabled(change.limitScheduleEnabled);
    }

    const updates = resolveExecutionScheduleUpdates({
      change,
      limitScheduleEnabled,
    });

    if (updates) {
      void onPersist({ updates, showSuccessToast: true });
    }
  };

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <SettingsSection
        title={t('sectionTitle')}
        description={t('sectionDescription')}
      >
        <ScanningConfigurationCard
          smokeTestEnabled={surface.smokeTestEnabled ?? false}
          limitScheduleEnabled={limitScheduleEnabled}
          automationSchedule={
            surface.executionSchedule ?? DEFAULT_SCANNING_SCHEDULE
          }
          runAutomatedLabel={t('runAutomated.label')}
          runAutomatedTooltip={t('runAutomated.tooltip')}
          limitScheduleLabel={t('limitSchedule.label')}
          limitScheduleTooltip={t('limitSchedule.tooltip')}
          disabled={isReadOnly}
          isUpdating={isUpdating}
          onChange={handleExecutionChange}
        />
      </SettingsSection>
    </VStack>
  );
}
